import React, {useCallback} from 'react';
import ListItem, {ListItemProps} from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Avatar from '@material-ui/core/Avatar';
import {makeStyles} from '@material-ui/core/styles';
import theme from '../../theme';
import isLayerTypeAuthorized from '../../utils/gaia/isLayerTypeAuthorized';
import useSyncPromise from '../../utils/react/useSyncPromise';
import fetchUser from '../../utils/gaia/fetchUser';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import {CONIFER} from '../../colors';
import InfoIcon from '@material-ui/icons/Info';

interface LayerListItemProps extends Omit<ListItemProps<'div'>, 'button'> {
    layerType: LayerType;
    secondary?: React.ReactNode;
    onSidebarChange: (sidebar: MapSidebar) => void;
    sidebar: MapSidebar;
}

export default class LayerListItemWrapper extends React.Component<LayerListItemProps> {
    render() {
        return <LayerListItem {...this.props} />;
    }
}

export const AVATAR_WIDTH = 48;

// eslint-disable-next-line prefer-arrow-callback
const LayerListItem = React.forwardRef(function LayerListItem({
    secondary,
    layerType,
    children,
    onSidebarChange,
    sidebar,
    style,
    ...props
}: LayerListItemProps, ref: any) {

    // eslint-disable-next-line no-use-before-define
    const classes = useStyles();

    const [user] = useSyncPromise(fetchUser());
    const isAuthorized = isLayerTypeAuthorized(layerType, user);

    return <ListItem
        style={{opacity: isAuthorized ? 1 : 0.5, ...style}}
        className={classes.layer}
        ref={ref}
        button
        onClick={useCallback(() => onSidebarChange({
            type: 'layerDetails',
            layerType,
            prevSidebar: sidebar,
        }), [layerType, onSidebarChange, sidebar])}
        {...props}
    >
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: AVATAR_WIDTH,
                height: AVATAR_WIDTH,
            }}
            onClick={useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                event.stopPropagation();

                onSidebarChange({
                    type: 'layerDetails',
                    layerType,
                    prevSidebar: sidebar,
                });
            }, [layerType, onSidebarChange, sidebar])}
        >
            <Avatar
                src={layerType.icon_url || ''}
                classes={{root: classes.avatar}}
                style={{filter: isAuthorized  ? 'none' : 'grayscale(1)'}}
            />
            <div className={classes.avatarInfo}>
                <InfoIcon />
            </div>
        </div>

        <ListItemText
            style={{paddingLeft: theme.spacing(1), margin: 0}}
            primary={layerType.title}
            secondary={<>
                {layerType.attribution}
                {secondary}
            </>}
            classes={{primary: classes.primary}}
        />
        <ListItemSecondaryAction>
            {children}
        </ListItemSecondaryAction>
    </ListItem>;
});

const useStyles = makeStyles({
    layer: {
        '& img': {
            '-webkit-user-drag': 'none',
            'user-drag': 'none',
        }
    },
    primary: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    avatar: {
        width: AVATAR_WIDTH,
        height: AVATAR_WIDTH,
        position: 'absolute',
    },
    avatarInfo: {
        width: AVATAR_WIDTH,
        height: AVATAR_WIDTH,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
        borderRadius: 6,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        color: 'rgba(0, 0, 0, 0)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            color: CONIFER,
        }
    }
});
